import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { showDenominations } from "./utils";

import { dashboardAPI, KpiFilters } from "../../../api/kpiCardsAPI";

import GlobalFilters from "./components/global-filters";

import KpiCard from "./components/kpi-card";

import SelectClientDropdown from "./components/select-client-dropdown";

import AccountActivityDetails from "./components/account-activity-details";

import PaymentActivityDetails from "./components/payment-activity-details";

import {
  StyledDashboardFilterBar,
  StyledDashboardSelectorSlot,
  StyledKpiCardsContainer,
  StyledPeriodLabel,
  StyledRootContainer,
  StyledTab,
  StyledTabPanelContainer,
  StyledTabsContainer,
  StyledTabsIndicator,
  SemiBoldText,
  RegularText,
} from "./styles";

import { Box, Dialog, DialogContent } from "@mui/material";

import { StyledDialogButton, StyledDialogHeading } from "./styles";

import { ReactComponent as NoDataFoundIcon } from "../../../assets/dashboard/NoDataFound.svg";

import {
  getCustomersForDashboardAPI,
  type Customer,
} from "../../../api/getCustomersAPI";

import colors from "../../../theme/Colours";

import { useRole } from "../../../context/RoleContext";

import SalesRepDropdown, { SalesRep } from "./components/sales-rep-dropdown";

import { fetchSalesReps } from "../../../api/customerAPI";

interface TabConfig {
  label: string;

  index: number;

  path: string;

  show: boolean;
}

interface CustomTabPanelProps {
  index: number;

  value: number;

  outletContext?: any;
}

interface KpiCardData {
  key: string;

  label: string;

  helperText: string;

  value: string;

  isPositive: boolean | null;

  valueColor?: string;
}

function CustomTabPanel({ index, value, outletContext }: CustomTabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={`dashboard-tabpanel-${index}`}
      hidden={value !== index}
      aria-labelledby={`dashboard-tab-${index}`}
    >
      {value === index && (
        <StyledTabPanelContainer>
          {/* Keep the child mounted while top dropdowns change so applied comparison filters are preserved. */}

          <Outlet key={`tab-${index}`} context={outletContext} />
        </StyledTabPanelContainer>
      )}
    </div>
  );
}

export default function SellerDashboard() {
  const navigate = useNavigate();

  const location = useLocation();

  const { role: contextRole, isRoleLoading } = useRole();

  const userEmail = localStorage.getItem("userEmail") || undefined;

  // Use context role first, fallback to localStorage to handle race condition

  const userRole = contextRole || localStorage.getItem("userRole") || undefined;

  const orgId = localStorage.getItem("orgId");

  const trialDaysRemaining = localStorage.getItem("trialDaysRemaining")
    ? parseInt(localStorage.getItem("trialDaysRemaining")!, 10)
    : undefined;

  console.log(
    "Dashboard - trialDaysRemaining from localStorage:",

    trialDaysRemaining
  );

  const tabConfig = useMemo(() => {
    const allTabs: TabConfig[] = [
      {
        label: "Sales Analytics",

        index: 0,

        path: "sales",

        show: true,
      },

      {
        label: "Sales Rep Analytics",

        index: 1,

        path: "salesrep-analytics",

        show: userRole !== "maltora-sales-rep",
      },

      {
        label: "Customer Analytics",

        index: 2,

        path: "client-analytics",

        show: userRole === "maltora-seller",
      },

      {
        label: "Finance Analytics",

        index: 3,

        path: "finance-analytics",

        show: userRole === "maltora-seller",
      },
    ];

    return allTabs.filter((tab) => tab.show);
  }, [userRole]);

  const getTabValueFromPath = useCallback((): number => {
    const path = location.pathname;

    const pathSegments = path.split("/").filter(Boolean);

    const lastSegment = pathSegments[pathSegments.length - 1];

    const allTabs = [
      { label: "Sales Analytics", index: 0, path: "sales" },

      { label: "Sales Rep Analytics", index: 1, path: "salesrep-analytics" },

      { label: "Customer Analytics", index: 2, path: "client-analytics" },

      { label: "Finance Analytics", index: 3, path: "finance-analytics" },
    ];

    const matchedTab = allTabs.find((tab) => tab.path === lastSegment);

    return matchedTab ? matchedTab.index : 0;
  }, [location.pathname]);

  const [value, setValue] = useState(() => getTabValueFromPath());

  const [clientStatus, setClientStatus] = useState<string | undefined>();

  const [kpiData, setKpiData] = useState<KpiCardData[]>([]);

  const [activeAccounts, setActiveAccounts] = useState<number>(0);

  const [lapsedAccounts, setLapsedAccounts] = useState<number>(0);

  const [inactiveAccounts, setInactiveAccounts] = useState<number>(0);

  const [globalFiltersFormKey, setGlobalFiltersFormKey] = useState(0);

  const [kpiCardsData, setKpiCardsData] = useState<any[]>([]);

  const [filtersApplied, setFiltersApplied] = useState(false);

  const [appliedFilterValues, setAppliedFilterValues] = useState<
    KpiFilters | undefined
  >(undefined);

  const [globalFiltersDisabled, setGlobalFiltersDisabled] = useState(false);

  const [comparisonFiltersDisabled, setComparisonFiltersDisabled] =
    useState(false);

  const [showNoDataPopup, setShowNoDataPopup] = useState(false);

  const [skipSalesAnalyticsAPI, setSkipSalesAnalyticsAPI] = useState(false);

  const [clientCustomerEmail, setClientCustomerEmail] = useState<
    string | undefined
  >(undefined);

  const [financeCustomerEmail, setFinanceCustomerEmail] = useState<
    string | undefined
  >(undefined);

  const [selectedSalesRepEmail, setSelectedSalesRepEmail] = useState("");

  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [hasCustomers, setHasCustomers] = useState(true);
  const kpiReqIdRef = useRef(0);

  //Accept explicit tabIndex so callers are never subject to stale closure on `value`

  const getDashboardType = useCallback(
    (tabIndex: number): string => {
      if (userRole === "maltora-sales-rep") return "maltora-sales-rep";

      const dashboardTypeMap: Record<number, string> = {
        0: "sales-analytics",

        1: "salesrep-analytics",

        2: "client-analytics",

        3: "finance-analytics",
      };

      return dashboardTypeMap[tabIndex] || "sales-analytics";
    },

    [userRole]
  );

  const dynamicDashboardType = getDashboardType(value);

  const customerEmail = clientCustomerEmail;

  useEffect(() => {
    if (dynamicDashboardType !== "client-analytics") {
      return;
    }

    let isMounted = true;

    const setDefaultClient = async () => {
      try {
        const response = await getCustomersForDashboardAPI("");

        const customers = response.customers || [];

        // NO CUSTOMERS
        if (!customers.length) {
          if (isMounted) {
            setHasCustomers(false);
            setClientCustomerEmail(undefined);
            setKpiData([]);
          }

          return;
        }

        // HAS CUSTOMERS
        setHasCustomers(true);

        if (clientCustomerEmail) {
          return;
        }

        const statusOrder: Record<string, number> = {
          active: 0,
          lapsed: 1,
          inactive: 2,
        };

        const firstClientEmail = customers
          .filter((customer: Customer) => customer.emailId)
          .sort((a: Customer, b: Customer) => {
            const aRank = statusOrder[(a.status || "").toLowerCase()] ?? 999;

            const bRank = statusOrder[(b.status || "").toLowerCase()] ?? 999;

            return aRank - bRank;
          })[0]?.emailId;

        if (isMounted && firstClientEmail) {
          setClientCustomerEmail(firstClientEmail);
        }
      } catch (error) {
        console.error("Failed to set default client", error);
        if (isMounted) {
          setHasCustomers(false);
          setClientCustomerEmail(undefined);
          setKpiData([]);
        }
      }
    };

    setDefaultClient();

    return () => {
      isMounted = false;
    };
  }, [dynamicDashboardType, clientCustomerEmail]);

  useEffect(() => {
    const loadSalesReps = async () => {
      if (dynamicDashboardType !== "salesrep-analytics") {
        setSalesReps([]);

        setSelectedSalesRepEmail("");

        return;
      }

      try {
        const reps: SalesRep[] = await fetchSalesReps();

        setSalesReps(reps);
      } catch (error) {
        console.error("Failed to fetch sales reps", error);

        setSalesReps([]);
      }
    };

    loadSalesReps();
  }, [dynamicDashboardType]);

  const getSellerEmail = (): string | undefined => {
    if (userRole === "maltora-sales-rep") return userEmail;

    if (dynamicDashboardType === "salesrep-analytics")
      return selectedSalesRepEmail || undefined;

    return undefined;
  };

  const sellerEmail = getSellerEmail();

  const handleClearSalesRepFilter = () => {
    setAppliedFilterValues((prev) => {
      if (prev?.salesRep) {
        const { salesRep, ...rest } = prev;

        return rest;
      }

      return prev;
    });

    setFiltersApplied((prev) => {
      if (prev && appliedFilterValues) {
        const { salesRep, ...rest } = appliedFilterValues;

        return Object.keys(rest).length > 0;
      }

      return prev;
    });
  };

  const formatGrowth = (value: string | number) => {
    const strValue = String(value);

    const isPositive = strValue.startsWith("+")
      ? true
      : strValue.startsWith("-")
        ? false
        : null;

    return {
      text: `${strValue} vs last month`,

      isPositive,
    };
  };

  //Accept explicit dashboardType so this never reads stale dynamicDashboardType from closure

  const buildKpiCardArray = (
    data: any,

    filtersAppliedFlag: boolean,

    resolvedDashboardType: string
  ): KpiCardData[] => {
    if (resolvedDashboardType === "finance-analytics") {
      return [
        {
          key: "totalRevenue",

          label: "Total Revenue",

          helperText: filtersAppliedFlag
            ? ""
            : formatGrowth(data.revenueGrowth).text,

          value: `${showDenominations(data.totalRevenue)}`,

          isPositive: formatGrowth(data.revenueGrowth).isPositive,
        },

        {
          key: "outstandingAmount",

          label: "Outstanding",

          helperText: "",

          value: `₹${showDenominations(Math.abs(data.outstandingAmount || 0))}`,

          isPositive: null,

          valueColor:
            data.outstandingAmount === 0
              ? colors.kpiCardNumbers
              : data.outstandingAmount > 0
                ? "#00B207"
                : "#FF0000",
        },
      ];
    }

    if (resolvedDashboardType === "client-analytics") {
      return [
        {
          key: "totalVolume",

          label: "Total sales volume",

          helperText: filtersAppliedFlag
            ? ""
            : formatGrowth(data.volumeGrowth).text,

          value: `${showDenominations(data.totalVolume)}L`,

          isPositive: formatGrowth(data.volumeGrowth).isPositive,
        },

        {
          key: "totalRevenue",

          label: "Total Revenue",

          helperText: filtersAppliedFlag
            ? ""
            : formatGrowth(data.revenueGrowth).text,

          value: `${showDenominations(data.totalRevenue)}`,

          isPositive: formatGrowth(data.revenueGrowth).isPositive,
        },

        {
          key: "orders",

          label: "Orders",

          helperText: filtersAppliedFlag
            ? ""
            : formatGrowth(data.orderGrowth).text,

          value: (data.orders ?? 0).toString(),

          isPositive: formatGrowth(data.orderGrowth).isPositive,
        },
      ];
    }

    return [
      {
        key: "totalVolume",

        label: "Total sales volume",

        helperText: filtersAppliedFlag
          ? ""
          : formatGrowth(data.volumeGrowth).text,

        value: `${showDenominations(data.totalVolume)}L`,

        isPositive: formatGrowth(data.volumeGrowth).isPositive,
      },

      {
        key: "totalRevenue",

        label: "Total Revenue",

        helperText: filtersAppliedFlag
          ? ""
          : formatGrowth(data.revenueGrowth).text,

        value: `${showDenominations(data.totalRevenue)}`,

        isPositive: formatGrowth(data.revenueGrowth).isPositive,
      },

      {
        key: "newOutlets",

        label:
          userRole === "maltora-sales-rep"
            ? "New Assigned Outlets"
            : "New Outlets",

        helperText: filtersAppliedFlag
          ? ""
          : formatGrowth(data.outletGrowth).text,

        value: data.newOutlets?.toString() || "0",

        isPositive: formatGrowth(data.outletGrowth).isPositive,
      },
    ];
  };

  //Accept explicit tabValue and snapshot of email state so this function never reads stale closed-over `value`, `customerEmail`, or `financeCustomerEmail`

  const fetchKpiData = async (
    filters?: KpiFilters,

    filtersAppliedFlag = false,

    customerEmailOverride?: string,

    tabValue: number = value,

    clientEmailSnapshot: string | undefined = clientCustomerEmail,

    financeEmailSnapshot: string | undefined = financeCustomerEmail
  ) => {
    const reqId = ++kpiReqIdRef.current;

    try {
      const dynamicType = getDashboardType(tabValue);

      //Scope customerEmail strictly per dashboard type — never bleed across tabs

      let activeCustomerEmail: string | undefined;

      if (customerEmailOverride !== undefined) {
        activeCustomerEmail = customerEmailOverride;
      } else if (dynamicType === "client-analytics") {
        activeCustomerEmail = clientEmailSnapshot;
      } else if (dynamicType === "finance-analytics") {
        activeCustomerEmail = financeEmailSnapshot;
      } else {
        activeCustomerEmail = undefined;
      }

      if (dynamicType === "client-analytics" && !activeCustomerEmail) {
        if (reqId === kpiReqIdRef.current) setKpiData([]);

        return;
      }

      const currentSellerEmail =
        userRole === "maltora-sales-rep"
          ? userEmail
          : dynamicType === "salesrep-analytics"
            ? selectedSalesRepEmail || undefined
            : undefined;

      const data = await dashboardAPI.getKPIDetails(
        filters,

        currentSellerEmail,

        dynamicType,

        dynamicType === "client-analytics" ||
          dynamicType === "finance-analytics"
          ? activeCustomerEmail
          : undefined
      );

      if (dynamicType === "client-analytics") {
        // Get status from customer list instead of KPI API for consistency

        const customerData = await getCustomersForDashboardAPI("");

        const selectedCustomer = customerData.customers.find(
          (c: Customer) => c.emailId === activeCustomerEmail
        );

        setClientStatus(selectedCustomer?.status || data?.status);
      } else {
        setClientStatus(undefined);
      }

      if (reqId !== kpiReqIdRef.current) return;

      // Extract account activity data if available (fallback for now)

      setActiveAccounts(data.activeAccounts || 0);

      setLapsedAccounts(data.lapsedAccounts || 0);

      setInactiveAccounts(data.inactiveAccounts || 0);

      // Also fetch KPI cards data to get order intervals analysis

      try {
        const kpiCardsResponse = await dashboardAPI.getKPICardsWithComparison(
          filters,

          currentSellerEmail,

          dynamicType,

          undefined,

          activeCustomerEmail
        );

        setKpiCardsData(kpiCardsResponse || []);
      } catch (error) {
        console.error("Failed to fetch KPI cards data:", error);

        setKpiCardsData([]);
      }

      // Pass resolvedDashboardType explicitly — never rely on closed-over dynamicDashboardType

      const kpiCardArray = buildKpiCardArray(
        data,

        filtersAppliedFlag,

        dynamicType
      );

      setKpiData(kpiCardArray);
    } catch (err) {
      if (reqId !== kpiReqIdRef.current) return;

      console.error("Error fetching KPI data:", err);
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = tabConfig.find((tab) => tab.index === newValue);

    if (!selectedTab) return;

    if (value === 1 && newValue === 0) handleClearSalesRepFilter();

    // Clear stale KPI data immediately so old tab's values don't persist

    setKpiData([]);

    //Reset tab-specific email state when leaving that tab, so it cannot bleed into the next tab's fetch

    if (selectedTab.path !== "client-analytics") {
      setClientCustomerEmail(undefined);
    }

    if (selectedTab.path !== "finance-analytics") {
      setFinanceCustomerEmail(undefined);
    }

    if (selectedTab.path !== "salesrep-analytics") {
      setSelectedSalesRepEmail("");
    }

    handleResetGlobalFilters("tab-change");

    setSkipSalesAnalyticsAPI(false);

    const parentRoute = `/maltora-seller/dashboard`;

    const newPath = `${parentRoute}/${selectedTab.path}`;

    navigate(newPath);
  };

  const handleSaveFilters = async (formData: any) => {
    const dynamicType = getDashboardType(value);

    const filters = {
      startDate: formData.fromDate,

      endDate: formData.toDate,

      licenseType: formData.licenseType,

      pinCode: formData.pinCode ?? formData.pincode,
    } as KpiFilters;

    const applied = !!(
      formData.fromDate ||
      formData.toDate ||
      formData.licenseType ||
      formData.pinCode ||
      formData.pincode
    );

    try {
      const cmCustomerEmail =
        dynamicType === "client-analytics"
          ? customerEmail
          : dynamicType === "finance-analytics"
            ? financeCustomerEmail
            : undefined;

      if (dynamicType === "client-analytics" && !cmCustomerEmail) {
        setKpiData([]);

        return;
      }

      const currentSellerEmail =
        userRole === "maltora-sales-rep"
          ? userEmail
          : dynamicType === "salesrep-analytics"
            ? selectedSalesRepEmail || undefined
            : undefined;

      const kpiDetails = await dashboardAPI.getKPIDetails(
        filters,

        currentSellerEmail,

        dynamicType,

        cmCustomerEmail
      );

      const kpiCardsResponse = await dashboardAPI.getKPICardsWithComparison(
        filters,

        currentSellerEmail,

        dynamicType,

        undefined,

        cmCustomerEmail
      );

      // Store KPI cards data for AccountActivityDetails component

      setKpiCardsData(kpiCardsResponse || []);

      const isAllZero =
        kpiDetails &&
        Object.values(kpiDetails).every((val) => val === "0" || val === 0);

      const isAllChartsEmpty =
        !kpiCardsResponse ||
        kpiCardsResponse.length === 0 ||
        kpiCardsResponse.every(
          (card: any) =>
            !card.isPeriodOneDataExists &&
            !card.isPeriodTwoDataExists &&
            !card.details?.find((d: any) => d.period === "current")?.values
              ?.length
        );

      if (isAllZero || isAllChartsEmpty) {
        setShowNoDataPopup(true);

        setSkipSalesAnalyticsAPI(true);

        return;
      }

      setAppliedFilterValues(filters);

      setSkipSalesAnalyticsAPI(false);

      setComparisonFiltersDisabled(applied);

      setFiltersApplied(applied);

      fetchKpiData(
        filters,

        applied,

        cmCustomerEmail,

        value,

        clientCustomerEmail,

        financeCustomerEmail
      );
    } catch (err) {
      console.error("Error fetching filtered KPI data:", err);
    }
  };

  type ResetSource = "button" | "tab-change";

  const handleResetGlobalFilters = (source: ResetSource = "button") => {
    setAppliedFilterValues(undefined);

    setFiltersApplied(false);

    setGlobalFiltersDisabled(false);

    setComparisonFiltersDisabled(false);

    setSkipSalesAnalyticsAPI(false);

    setGlobalFiltersFormKey((k) => k + 1);

    setKpiCardsData([]);

    if (source === "tab-change") {
      setKpiData([]);

      kpiReqIdRef.current++;

      return;
    }

    if (source === "button") {
      if (dynamicDashboardType === "client-analytics" && !customerEmail) {
        setKpiData([]);

        kpiReqIdRef.current++;

        return;
      }
    }

    // Pass current value and email snapshots explicitly

    fetchKpiData(
      undefined,

      false,

      undefined,

      value,

      clientCustomerEmail,

      financeCustomerEmail
    );
  };

  useEffect(() => {
    const newTabValue = getTabValueFromPath();

    setValue(newTabValue);
  }, [location.pathname, getTabValueFromPath]);

  // Pass the up-to-date tabValue and email snapshots explicitly into fetchKpiData

  useEffect(() => {
    const currentTabValue = getTabValueFromPath();

    const currentDashboardType = getDashboardType(currentTabValue);

    if (currentDashboardType === "client-analytics" && !clientCustomerEmail) {
      setKpiData([]);

      return;
    }

    fetchKpiData(
      appliedFilterValues,

      filtersApplied,

      undefined,

      currentTabValue,

      clientCustomerEmail,

      financeCustomerEmail
    );
  }, [
    value,

    clientCustomerEmail,

    financeCustomerEmail,

    selectedSalesRepEmail,

    appliedFilterValues,

    filtersApplied,

    getTabValueFromPath,

    getDashboardType,
  ]);

  const outletContext = useMemo(
    () => ({
      globalFilters: appliedFilterValues,

      skipSalesAnalyticsAPI,

      comparisonFiltersDisabled,

      setComparisonFiltersDisabled,

      globalFiltersDisabled,

      setGlobalFiltersDisabled,

      currentTab: dynamicDashboardType,

      dashboardType: dynamicDashboardType,

      sellerEmail,

      customerEmail:
        dynamicDashboardType === "finance-analytics"
          ? financeCustomerEmail
          : customerEmail,

      setClientAnalyticsCustomerEmail: setClientCustomerEmail,
    }),

    [
      appliedFilterValues,

      skipSalesAnalyticsAPI,

      comparisonFiltersDisabled,

      setComparisonFiltersDisabled,

      globalFiltersDisabled,

      setGlobalFiltersDisabled,

      dynamicDashboardType,

      sellerEmail,

      customerEmail,

      financeCustomerEmail,

      selectedSalesRepEmail,

      setClientCustomerEmail,
    ]
  );

  // Show loading state while role is being determined

  if (isRoleLoading) {
    return <div>Loading dashboard...</div>;
  }

  // Redirect if no valid role is found

  if (!userRole) {
    navigate(`/signin`);

    return null;
  }

  return (
    <>
      <StyledRootContainer>
        {/* Filter Controls Section */}

        <StyledDashboardFilterBar>
          <GlobalFilters
            key={globalFiltersFormKey}
            onSave={handleSaveFilters}
            onReset={() => handleResetGlobalFilters("button")}
            disabled={globalFiltersDisabled}
            resetDisabled={!filtersApplied}
            trialDaysRemaining={trialDaysRemaining}
          />
        </StyledDashboardFilterBar>

        {/* Tab Navigation Section and Dropdowns */}

        <StyledTabsContainer>
          <StyledTabsIndicator value={value} onChange={handleChange}>
            {tabConfig.map((tab) => (
              <StyledTab
                key={`tab-${tab.index}`}
                id={`dashboard-tab-${tab.index}`}
                label={tab.label}
                aria-controls={`dashboard-tabpanel-${tab.index}`}
              />
            ))}
          </StyledTabsIndicator>

          {dynamicDashboardType === "client-analytics" && hasCustomers && (
            <StyledDashboardSelectorSlot>
              <StyledPeriodLabel>Select Customer</StyledPeriodLabel>

              <SelectClientDropdown
                selectedClient={clientCustomerEmail || ""}
                setSelectedClient={(email) =>
                  setClientCustomerEmail(email || undefined)
                }
                isClientAnalytics
                sortByStatus
                disableTopPadding
              />
            </StyledDashboardSelectorSlot>
          )}

          {dynamicDashboardType === "salesrep-analytics" && (
            <StyledDashboardSelectorSlot>
              <StyledPeriodLabel>Select Sales Rep</StyledPeriodLabel>

              <SalesRepDropdown
                selectedSalesRep={selectedSalesRepEmail}
                setSelectedSalesRep={setSelectedSalesRepEmail}
                salesReps={salesReps}
                dashboardType={dynamicDashboardType}
              />
            </StyledDashboardSelectorSlot>
          )}

          {dynamicDashboardType === "finance-analytics" && (
            <StyledDashboardSelectorSlot>
              <StyledPeriodLabel>Select Customer</StyledPeriodLabel>

              <SelectClientDropdown
                selectedClient={financeCustomerEmail || ""}
                setSelectedClient={(email) =>
                  setFinanceCustomerEmail(email || undefined)
                }
                sortByStatus
                disableTopPadding
              />
            </StyledDashboardSelectorSlot>
          )}
        </StyledTabsContainer>

        {dynamicDashboardType === "client-analytics" &&
          ["inactive", "lapsed"].includes(
            (clientStatus || "").toLowerCase()
          ) && (
            <Box
              sx={{
                mx: 3,

                mt: 2,

                px: 2,

                py: 1.5,

                borderRadius: "8px",

                border: "1px solid #FFB4A8",

                backgroundColor: colors.primaryButtonOrangeLight,

                display: "flex",

                alignItems: "center",

                justifyContent: "space-between",

                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ color: colors.regressColorKpi, fontSize: 16 }}>
                  ⚠
                </Box>

                <SemiBoldText style={{ color: colors.regressColorKpi }}>
                  Client is {(clientStatus || "").toLowerCase()}
                </SemiBoldText>

                <RegularText style={{ color: colors.kpiCardLabels }}>
                  {(clientStatus || "").toLowerCase() === "inactive"
                    ? "This account has no recent orders. Contact your sales rep to reactivate."
                    : "This account has reduced activity recently. Consider re-engaging the client."}
                </RegularText>
              </Box>

              <SemiBoldText
                style={{
                  color: colors.regressColorKpi,

                  fontSize: 16,

                  textTransform: "capitalize",
                }}
              >
                • {(clientStatus || "").toLowerCase()}
              </SemiBoldText>
            </Box>
          )}

        {/* KPI Cards Section */}

        {/* KPI Cards Section */}

        {!(dynamicDashboardType === "client-analytics" && !hasCustomers) && (
          <StyledKpiCardsContainer>
            {kpiData.map(
              ({
                key,

                label,

                helperText,

                value: kpiValue,

                isPositive,

                valueColor,
              }) => (
                <KpiCard
                  key={`dashboard_kpi_${key}`}
                  label={label}
                  helperText={helperText}
                  value={kpiValue}
                  isPositive={isPositive}
                  valueColor={valueColor}
                />
              )
            )}

            {/* Add Account Activity Details card for sales analytics and Payment Activity Details for finance analytics
          - always render to prevent layout shifts
        */}

            {dynamicDashboardType === "sales-analytics" ? (
              <AccountActivityDetails
                orderIntervalsAnalysis={kpiCardsData.find(
                  (card) => card.key === "orderIntervalsAnalysis"
                )}
              />
            ) : dynamicDashboardType === "finance-analytics" ? (
              <PaymentActivityDetails
                paymentIntervalAnalysis={kpiCardsData.find(
                  (card) => card.key === "paymentIntervalAnalysis"
                )}
              />
            ) : (
              <AccountActivityDetails orderIntervalsAnalysis={undefined} />
            )}
          </StyledKpiCardsContainer>
        )}

        {/* Tab Panels Section */}

        {/* Tab Panels Section */}

        {dynamicDashboardType === "client-analytics" && !hasCustomers ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
              width: "100%",
            }}
          >
            <StyledDialogHeading>No Data available</StyledDialogHeading>
          </Box>
        ) : (
          tabConfig.map((tab) => (
            <CustomTabPanel
              key={`panel-${tab.index}`}
              value={value}
              index={tab.index}
              outletContext={outletContext}
            />
          ))
        )}
      </StyledRootContainer>

      {/* No Data Dialog */}

      <Dialog
        open={showNoDataPopup}
        onClose={() => {
          setShowNoDataPopup(false);

          setSkipSalesAnalyticsAPI(false);
        }}
        PaperProps={{
          sx: {
            borderRadius: "16px",

            p: 2,

            textAlign: "center",

            width: "100%",

            maxWidth: 400,
          },
        }}
        aria-labelledby="no-data-dialog"
      >
        <DialogContent>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
            <NoDataFoundIcon width={40} height={40} />
          </Box>

          <StyledDialogHeading id="no-data-dialog">
            No Data available for selected range
          </StyledDialogHeading>

          <StyledDialogButton
            variant="contained"
            onClick={() => {
              setShowNoDataPopup(false);

              setSkipSalesAnalyticsAPI(false);
            }}
          >
            OK
          </StyledDialogButton>
        </DialogContent>
      </Dialog>
    </>
  );
}
